import type { FreshnessMeta } from "@/lib/api";
import { resolveFreshness } from "@/lib/freshness";

interface FreshnessLineProps {
  /** The S8 envelope's `meta`. Absent is a legitimate input, not an error. */
  meta?: FreshnessMeta | null;
  /** True while the module response is still in flight (UX S4 partial). */
  pending?: boolean;
  /** Injected only by tests, so the stale boundary is assertable. */
  now?: number;
}

/**
 * The freshness line — the physical form of BR-11 (invariant I-6).
 *
 * Shell-owned and non-optional: a module never renders its own. It is a
 * `status` landmark and it is never `text-ks-grey-*` — this line is
 * load-bearing, not secondary text (UX v0.3 §7 contrast intent).
 *
 * Three states and no fourth: normal, stale, unknown. There is deliberately no
 * "fresh" branch — nothing here ever ASSERTS currency, it only reports the
 * date the module gave and whether that date is older than the module itself
 * said to expect.
 */
export function FreshnessLine({ meta, pending = false, now }: FreshnessLineProps) {
  if (pending) {
    // UX S4 partial: the slot is occupied and says what it is doing. A blank
    // slot beside rendered content is exactly the implied-fresh read we ban.
    return (
      <p role="status" className="py-2 text-sm text-ks-ink">
        Checking data date…
      </p>
    );
  }

  const freshness = resolveFreshness(meta, now);

  return (
    <p
      role="status"
      aria-live="polite"
      data-freshness={freshness.kind}
      // Stale is carried by the SENTENCE ("· older than expected") and only
      // reinforced by colour — a colour-only stale signal is not sufficient
      // (UX v0.3 §7 announcements), and would be invisible to a screen reader.
      // Red is reserved for danger; late data is a caution, so it takes the
      // warning tone and the rest of the line stays quiet.
      className={`py-2 text-xs ${freshness.kind === "stale" ? "text-ks-warning" : "text-muted-foreground"}`}
      title={freshness.source ? `Source: ${freshness.source}` : undefined}
    >
      {freshness.text}
    </p>
  );
}
